import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

interface DeviceInfo {
  deviceInfoId: number;
  deviceType: string;
  deviceBrand: string;
  devicePlatform: string;
  deviceOsVersion: number;
  deviceOsApi: number;
  webUa: string;
  cpuAbi: string;
  resolutionWidth: number;
  resolutionHeight: number;
  dpi: number;
}

interface CreateDeviceInfoRequest {
  deviceType: string;
  deviceBrand: string;
  devicePlatform: string;
  deviceOsVersion: number;
  deviceOsApi: number;
  webUa: string;
  cpuAbi: string;
  resolutionWidth: number;
  resolutionHeight: number;
  dpi: number;
}

const DeviceManager: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState<DeviceInfo | null>(null);
  const [deviceForm, setDeviceForm] = useState<CreateDeviceInfoRequest>({
    deviceType: '',
    deviceBrand: '',
    devicePlatform: '',
    deviceOsVersion: 0,
    deviceOsApi: 0,
    webUa: '',
    cpuAbi: '',
    resolutionWidth: 0,
    resolutionHeight: 0,
    dpi: 0
  });

  const queryClient = useQueryClient();

  // Fetch devices
  const { data: devices = [], isLoading } = useQuery<DeviceInfo[]>({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await fetch('/api/device-infos');
      if (!response.ok) throw new Error('Failed to fetch devices');
      const backendData = await response.json();
      
      // Transform snake_case to camelCase for frontend
      return backendData.map((device: any) => ({
        deviceInfoId: device.device_info_id,
        deviceType: device.device_type,
        deviceBrand: device.device_brand,
        devicePlatform: device.device_platform,
        deviceOsVersion: device.device_os_version,
        deviceOsApi: device.device_os_api,
        webUa: device.web_ua,
        cpuAbi: device.cpu_abi,
        resolutionWidth: device.resolution_width,
        resolutionHeight: device.resolution_height,
        dpi: device.dpi
      }));
    }
  });

  // Create device mutation
  const createDeviceMutation = useMutation({
    mutationFn: async (deviceData: CreateDeviceInfoRequest) => {
      // Transform camelCase to snake_case for backend
      const backendData = {
        device_type: deviceData.deviceType,
        device_brand: deviceData.deviceBrand,
        device_platform: deviceData.devicePlatform,
        device_os_version: deviceData.deviceOsVersion,
        device_os_api: deviceData.deviceOsApi,
        web_ua: deviceData.webUa,
        cpu_abi: deviceData.cpuAbi,
        resolution_width: deviceData.resolutionWidth,
        resolution_height: deviceData.resolutionHeight,
        dpi: deviceData.dpi
      };

      const response = await fetch('/api/device-infos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData),
      });
      if (!response.ok) {
        throw new Error(`Failed to create device: ${response.status}`);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      alert('Failed to create device. Please try again.');
    }
  });

  // Update device mutation
  const updateDeviceMutation = useMutation({
    mutationFn: async ({ deviceInfoId, deviceData }: { deviceInfoId: number; deviceData: CreateDeviceInfoRequest }) => {
      // Transform camelCase to snake_case for backend
      const backendData = {
        device_type: deviceData.deviceType,
        device_brand: deviceData.deviceBrand,
        device_platform: deviceData.devicePlatform,
        device_os_version: deviceData.deviceOsVersion,
        device_os_api: deviceData.deviceOsApi,
        web_ua: deviceData.webUa,
        cpu_abi: deviceData.cpuAbi,
        resolution_width: deviceData.resolutionWidth,
        resolution_height: deviceData.resolutionHeight,
        dpi: deviceData.dpi
      };

      const response = await fetch(`/api/device-infos/${deviceInfoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(backendData),
      });
      if (!response.ok) {
        throw new Error(`Failed to update device: ${response.status}`);
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
      setIsModalOpen(false);
      setEditingDevice(null);
      resetForm();
    },
    onError: (error) => {
      alert('Failed to update device. Please try again.');
    }
  });

  // Delete device mutation
  const deleteDeviceMutation = useMutation({
    mutationFn: async (deviceInfoId: number) => {
      const response = await fetch(`/api/device-infos/${deviceInfoId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Failed to delete device');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
    onError: (error) => {
      console.error('Error deleting device:', error);
      alert('Failed to delete device. Please try again.');
    }
  });

  const resetForm = () => {
    setDeviceForm({
      deviceType: '',
      deviceBrand: '',
      devicePlatform: '',
      deviceOsVersion: 0,
      deviceOsApi: 0,
      webUa: '',
      cpuAbi: '',
      resolutionWidth: 0,
      resolutionHeight: 0,
      dpi: 0
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingDevice) {
      updateDeviceMutation.mutate({ 
        deviceInfoId: editingDevice.deviceInfoId, 
        deviceData: deviceForm 
      });
    } else {
      createDeviceMutation.mutate(deviceForm);
    }
  };

  const handleEdit = (device: DeviceInfo) => {
    setEditingDevice(device);
    setDeviceForm({
      deviceType: device.deviceType,
      deviceBrand: device.deviceBrand,
      devicePlatform: device.devicePlatform,
      deviceOsVersion: device.deviceOsVersion,
      deviceOsApi: device.deviceOsApi,
      webUa: device.webUa,
      cpuAbi: device.cpuAbi,
      resolutionWidth: device.resolutionWidth,
      resolutionHeight: device.resolutionHeight,
      dpi: device.dpi
    });
    setIsModalOpen(true);
  };

  const handleDelete = (deviceInfoId: number) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      deleteDeviceMutation.mutate(deviceInfoId);
    }
  };

  const openCreateModal = () => {
    setEditingDevice(null);
    resetForm();
    setIsModalOpen(true);
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
        setEditingDevice(null);
        resetForm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Pre-populate form when editing
  useEffect(() => {
    if (editingDevice && isModalOpen) {
      setDeviceForm({
        deviceType: editingDevice.deviceType,
        deviceBrand: editingDevice.deviceBrand,
        devicePlatform: editingDevice.devicePlatform,
        deviceOsVersion: editingDevice.deviceOsVersion,
        deviceOsApi: editingDevice.deviceOsApi,
        webUa: editingDevice.webUa,
        cpuAbi: editingDevice.cpuAbi,
        resolutionWidth: editingDevice.resolutionWidth,
        resolutionHeight: editingDevice.resolutionHeight,
        dpi: editingDevice.dpi
      });
    }
  }, [editingDevice, isModalOpen]);

  if (isLoading) return <div>Loading devices...</div>;

  return (
    <div className="component-container">
      <div className="component-header">
        <h1>📱 Device Management</h1>
        <p>Manage device configurations for bot simulation</p>
      </div>
      
      <div className="device-manager-page">
        <div className="device-manager">
          <div className="device-manager-header">
            <h2>Device Configurations</h2>
            <button onClick={openCreateModal} className="btn btn-primary">
              Create New Device
            </button>
          </div>

          <div className="devices-grid">
            {devices.map((device: DeviceInfo) => (
              <div key={device.deviceInfoId} className="device-card">
                <div className="device-card-header">
                  <h3>{device.deviceBrand} {device.deviceType}</h3>
                  <div className="device-actions">
                    <button onClick={() => handleEdit(device)} className="btn btn-secondary">
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(device.deviceInfoId)} 
                      className="btn btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="device-details">
                  <p><strong>Platform:</strong> {device.devicePlatform}</p>
                  <p><strong>OS Version:</strong> {device.deviceOsVersion}</p>
                  <p><strong>API Level:</strong> {device.deviceOsApi}</p>
                  <p><strong>CPU ABI:</strong> {device.cpuAbi}</p>
                  <p><strong>Resolution:</strong> {device.resolutionWidth}x{device.resolutionHeight}</p>
                  <p><strong>DPI:</strong> {device.dpi}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content device-modal-compact" onClick={(e) => e.stopPropagation()}>
            <h3>{editingDevice ? 'Edit Device' : 'Create New Device'}</h3>
            <form onSubmit={handleSubmit} className="device-form-compact">
              <div className="form-row">
                <div className="form-group">
                  <label>Device Type:</label>
                  <input
                    type="text"
                    value={deviceForm.deviceType}
                    onChange={(e) => setDeviceForm({...deviceForm, deviceType: e.target.value})}
                    required
                    placeholder="e.g., sdk_gphone64_x86_64"
                  />
                </div>
                <div className="form-group">
                  <label>Device Brand:</label>
                  <input
                    type="text"
                    value={deviceForm.deviceBrand}
                    onChange={(e) => setDeviceForm({...deviceForm, deviceBrand: e.target.value})}
                    required
                    placeholder="e.g., Samsung, Google"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Platform:</label>
                  <input
                    type="text"
                    value={deviceForm.devicePlatform}
                    onChange={(e) => setDeviceForm({...deviceForm, devicePlatform: e.target.value})}
                    required
                    placeholder="e.g., Android, iOS"
                  />
                </div>
                <div className="form-group">
                  <label>OS Version:</label>
                  <input
                    type="number"
                    value={deviceForm.deviceOsVersion}
                    onChange={(e) => setDeviceForm({...deviceForm, deviceOsVersion: parseInt(e.target.value)})}
                    required
                    placeholder="e.g., 31"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>API Level:</label>
                  <input
                    type="number"
                    value={deviceForm.deviceOsApi}
                    onChange={(e) => setDeviceForm({...deviceForm, deviceOsApi: parseInt(e.target.value)})}
                    required
                    placeholder="e.g., 31"
                  />
                </div>
                <div className="form-group">
                  <label>CPU ABI:</label>
                  <input
                    type="text"
                    value={deviceForm.cpuAbi}
                    onChange={(e) => setDeviceForm({...deviceForm, cpuAbi: e.target.value})}
                    required
                    placeholder="e.g., x86_64, arm64-v8a"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Web User Agent:</label>
                <textarea
                  value={deviceForm.webUa}
                  onChange={(e) => setDeviceForm({...deviceForm, webUa: e.target.value})}
                  required
                  placeholder="Mozilla/5.0 (Linux; Android 11; sdk_gphone64_x86_64)..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Resolution Width:</label>
                  <input
                    type="number"
                    value={deviceForm.resolutionWidth}
                    onChange={(e) => setDeviceForm({...deviceForm, resolutionWidth: parseInt(e.target.value)})}
                    required
                    placeholder="e.g., 1080"
                  />
                </div>
                <div className="form-group">
                  <label>Resolution Height:</label>
                  <input
                    type="number"
                    value={deviceForm.resolutionHeight}
                    onChange={(e) => setDeviceForm({...deviceForm, resolutionHeight: parseInt(e.target.value)})}
                    required
                    placeholder="e.g., 1920"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>DPI:</label>
                <input
                  type="number"
                  value={deviceForm.dpi}
                  onChange={(e) => setDeviceForm({...deviceForm, dpi: parseInt(e.target.value)})}
                  required
                  placeholder="e.g., 420"
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setIsModalOpen(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={createDeviceMutation.isPending || updateDeviceMutation.isPending}
                >
                  {editingDevice ? 'Update' : 'Create'} Device
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManager; 